import { sentenceCase } from "@redotech/string/transform";
import { Step } from "../return-flow";
import { FlowType } from "../return-flow-enums";

type StepTypeSymbol = Extract<(typeof Step)[keyof typeof Step], symbol>;

/**
 * Step names as merchants see them in the flow editors, surfaced in flow-edit
 * approval summaries; keyed by Step symbols so an unnamed step cannot compile.
 */
export const STEP_TYPE_NAMES: Record<StepTypeSymbol, string> = {
  [Step.ACTION]: "Action",
  [Step.ADD_CONVERSATION_TAG]: "Add conversation tag",
  [Step.ADD_EXCHANGE_ORDER_TAGS]: "Add exchange order tags",
  [Step.ADD_RETURN_TAG]: "Add return tag",
  [Step.ADD_UNDECLARED_ITEM]: "Add undeclared item",
  [Step.AB_TEST]: "A/B test",
  [Step.AI_RETURN_REASONS]: "AI Return Reasons",
  [Step.ASSIGN_TO_AGENT]: "Assign to support agent",
  [Step.BLOCK]: "Block",
  [Step.CHAT_MULTIPLE_CHOICE]: "Multiple choice",
  [Step.CLOSE_CHAT]: "Close chat",
  [Step.COMBINE_LABEL]: "Combine label",
  [Step.CONDITION]: "Condition",
  [Step.CUSTOMER_RESPONSE]: "Customer response",
  [Step.DISCOUNT_CODE_NAME]: "Discount code name",
  [Step.DO_NOTHING]: "Do nothing",
  [Step.DYNAMIC_BUNDLE_ADJUSTMENT]: "Dynamic bundle adjustment",
  [Step.EVIDENCE]: "Capture evidence",
  [Step.EW_UPSELL]: "Extended warranty upsell",
  [Step.EXCHANGE_SHIPPING_NAME]: "Shipping name",
  [Step.EXCHANGE_SHIPPING_RATES]: "Exchange shipping rates",
  [Step.FINAL_SALE_RETURN]: "Mark as final sale return",
  [Step.FLAG]: "Flag",
  [Step.INFORMATION]: "Information",
  [Step.INPUT]: "Input",
  [Step.MAKE_SERVICE_REQUEST]: "Make service request",
  [Step.MANUAL_REVIEW]: "Mark for manual review",
  [Step.MERCHANT_REVIEW_BEFORE_FILING]: "Review before filing claim",
  [Step.MULTIPLE_ACTIONS]: "Actions",
  [Step.MULTIPLE_CHOICE]: "Multiple choice",
  [Step.MULTIPLE_CONDITIONS]: "Conditions",
  [Step.OVERRIDE_ADJUSTMENT]: "Override Adjustment",
  [Step.OVERRIDE_GREEN_RETURN]: "Require send back",
  [Step.PROCESSING_ACTION]: "Assign grade",
  [Step.REDO_MANAGED_LOCATION]: "Returns Center",
  [Step.REENTRY]: "Base question",
  [Step.REJECT]: "Reject",
  [Step.RETURN]: "Return",
  [Step.RETURN_PROCESSING_DECISION]: "Decision",
  [Step.RETURN_PROCESSING_INFORMATION]: "Information",
  [Step.SEE_ORDERS]: "See orders",
  [Step.SEND_EMAIL]: "Send Email",
  [Step.SEND_MESSAGE]: "Send message",
  [Step.SEND_SMS]: "Send SMS Message",
  [Step.SEPARATE_LABELS]: "Separate labels",
  [Step.SET_RETURN_LOCATIONS]: "Location",
  [Step.SORT]: "Sort",
  [Step.SUBMIT_CLAIM]: "Submit Claim",
  [Step.SUBMIT_REPAIR]: "Submit Repair",
  [Step.SUBMIT_WARRANTY]: "Submit Warranty",
  [Step.SUBMIT_WARRANTY_REGISTRATION]: "Submit Warranty Registration",
  [Step.TRIGGER]: "Trigger",
  [Step.VERIFICATION]: "Agent verification",
  [Step.VERIFY_ITEM_VARIANT]: "Verify item variant",
  [Step.WAIT]: "Wait",
};

/**
 * Display name for the Return step in a given flow. The claim and warranty
 * flows offer the same step as "Submit Advanced Claim" / "Submit Advanced
 * Warranty" so merchants can tell the multi-option outcome apart from the
 * fixed-outcome Submit Claim / Submit Warranty steps.
 */
export function returnStepTypeName(flowType: FlowType | undefined): string {
  switch (flowType) {
    case FlowType.CLAIM:
      return "Submit Advanced Claim";
    case FlowType.WARRANTY:
      return "Submit Advanced Warranty";
    default:
      return STEP_TYPE_NAMES[Step.RETURN];
  }
}

const NAMES_BY_WIRE_TYPE = new Map<string, string>();
for (const value of Object.values(Step)) {
  if (typeof value === "symbol" && value.description) {
    NAMES_BY_WIRE_TYPE.set(value.description, STEP_TYPE_NAMES[value]);
  }
}

/** Display name for a wire-format step type string, e.g. "manual_review". */
export function stepTypeName(type: unknown): string {
  if (typeof type !== "string" || !type) {
    return "Step";
  }
  return NAMES_BY_WIRE_TYPE.get(type) ?? sentenceCase(type);
}
